import { useEffect } from 'react'

import { emrSession } from '~/shell/auth/emrSession'

import styles from './LaunchNotice.module.css'

interface LaunchNoticeProps {
  /** The heading, naming what happened in the user's terms. */
  heading: string
  /** The body copy, whose job is to name the one action the user can take. */
  message: string
}

/**
 * The card the two terminal launch pages share.
 *
 * Reaching either one ends the EMR-embedded session, which is why the clear happens here rather than in
 * each caller: the token (if any) cannot be used and cannot be renewed, and leaving the record would
 * hold the browser in EMR-embedded mode, where the login gate keeps redirecting back to this page instead
 * of offering the standalone login.
 *
 * Both pages carry copy alone. Neither offers a retry, because the EHR owns the launch: the suite
 * cannot start one, and a "try again" that reloaded a spent handoff code would only fail again.
 */
const LaunchNotice = ({ heading, message }: LaunchNoticeProps) => {
  useEffect(() => {
    emrSession.clear()
  }, [])

  return (
    <main className={styles.container}>
      <div className={styles.card} role="alert">
        <h1 className={styles.heading}>{heading}</h1>
        <p className={styles.message}>{message}</p>
      </div>
    </main>
  )
}

/**
 * Shown for a launch that failed for a reason that may not recur, and for an EMR-embedded session whose
 * token has expired. One page for both, because the user's move is the same either way.
 */
export const LaunchFailedPage = () => (
  <LaunchNotice
    heading="Regard needs to be reopened"
    message="We couldn’t start your Regard session. Go back to the patient’s chart in your EHR and open Regard again."
  />
)

/**
 * Shown for a launch that cannot succeed for this user however often it is retried — an EHR the suite
 * does not serve, or an account with no Regard access.
 */
export const LaunchNotAuthorizedPage = () => (
  <LaunchNotice
    heading="No access"
    message="Your account isn’t set up to use Regard from your EHR. Contact Regard support if you think this is a mistake."
  />
)
